import { z } from "zod";
import { RequiredFirestoreTimestampSchema } from "./common.schema";

/**
 * Integration status enum
 */
export const IntegrationStatusSchema = z.enum([
  "connected",
  "disconnected",
  "error",
  "expired",
]);

export type IntegrationStatus = z.infer<typeof IntegrationStatusSchema>;

/**
 * Google Analytics Property schema
 */
export const GoogleAnalyticsPropertySchema = z.object({
  property_id: z.string(),
  display_name: z.string(),
  account_id: z.string(),
  time_zone: z.string().nullable().optional(),
  currency_code: z.string().nullable().optional(),
});

export type GoogleAnalyticsProperty = z.infer<
  typeof GoogleAnalyticsPropertySchema
>;

/**
 * Google Analytics token reference schema.
 * Raw OAuth tokens live in Secret Manager; Firestore stores secret names.
 */
export const GoogleAnalyticsTokenRefSchema = z.object({
  access_token_secret: z.string(),
  refresh_token_secret: z.string(),
  expires_at: RequiredFirestoreTimestampSchema,
  scopes: z.array(z.string()),
  token_uri: z.string().default("https://oauth2.googleapis.com/token"),
});

export type GoogleAnalyticsTokenRef = z.infer<
  typeof GoogleAnalyticsTokenRefSchema
>;

/**
 * Google Analytics Account schema (Firestore document)
 */
export const GoogleAnalyticsAccountSchema = z.object({
  account_id: z.string(),
  account_email: z.string().email(),
  account_name: z.string().nullable().default(null),
  token_ref: GoogleAnalyticsTokenRefSchema.nullable().default(null),
  properties: z.array(GoogleAnalyticsPropertySchema).default([]),
  client_id: z.string(),
  status: IntegrationStatusSchema.default("connected"),
  error_message: z.string().nullable().default(null),
  created_at: RequiredFirestoreTimestampSchema.nullable().default(null),
  updated_at: RequiredFirestoreTimestampSchema.nullable().default(null),
});

export type GoogleAnalyticsAccount = z.infer<
  typeof GoogleAnalyticsAccountSchema
>;

/**
 * Parse function for GoogleAnalyticsAccount
 */
export const parseGoogleAnalyticsAccount = (
  data: unknown,
): GoogleAnalyticsAccount => {
  return GoogleAnalyticsAccountSchema.parse(data);
};

/**
 * Safe parse function for GoogleAnalyticsAccount
 */
export const safeGoogleAnalyticsAccountParse = (data: unknown) => {
  return GoogleAnalyticsAccountSchema.safeParse(data);
};

// Note: Other GoogleAnalytics types (Response types, Request types, UI State, etc.)
// are not stored in Firestore, so they don't need Zod schemas at this time.
// They remain as TypeScript types in src/types/GoogleAnalytics.ts
